import { useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronLeft } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { Step7Data } from '../../../types/onboarding';
import {
  INDUSTRY_TAXONOMY,
  getCategory,
  getQuestionsForIndustry,
} from '../../../constants/industry-taxonomy';

interface Step7Props {
  data: Step7Data | null;
  onChange: (data: Step7Data) => void;
  onNext: () => void;
  onBack: () => void;
  saving: boolean;
  /** When true, shows welcome message and hides back button (used when this is UI step 1) */
  isFirstStep?: boolean;
  /** Business name for welcome message */
  businessName?: string;
}

type ViewState = 'category' | 'type' | 'questions';

export function Step7Industry({
  data,
  onChange,
  onNext,
  onBack,
  saving,
  isFirstStep = false,
  businessName,
}: Step7Props) {
  // View state for the 2-tier selection flow
  const [view, setView] = useState<ViewState>(() => {
    if (data?.industry_category && data?.industry_type) {
      return 'questions';
    }
    if (data?.industry_category) {
      return 'type';
    }
    return 'category';
  });

  // Selection state
  const [selectedCategory, setSelectedCategory] = useState<string>(data?.industry_category || '');
  const [otherCategoryText, setOtherCategoryText] = useState<string>(data?.industry_category_other || '');
  const [selectedType, setSelectedType] = useState<string>(data?.industry_type || '');
  const [otherTypeText, setOtherTypeText] = useState<string>(data?.industry_type_other || '');

  // Question answers state
  const [answers, setAnswers] = useState<Record<string, unknown>>(data?.industry_specific || {});
  const [multiSelectValues, setMultiSelectValues] = useState<Record<string, string[]>>(() => {
    // Initialize multiselect values from existing data
    const msValues: Record<string, string[]> = {};
    if (data?.industry_specific) {
      Object.entries(data.industry_specific).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          msValues[key] = value as string[];
        }
      });
    }
    return msValues;
  });

  // Get current category and its types
  const currentCategory = getCategory(selectedCategory);
  const questions = selectedCategory && selectedType
    ? getQuestionsForIndustry(selectedCategory, selectedType)
    : [];

  // Save data to parent — filters out stale fields from previous type selections
  const saveData = (
    category: string,
    categoryOther: string,
    type: string,
    typeOther: string,
    questionAnswers: Record<string, unknown>,
    multiSelect: Record<string, string[]>
  ) => {
    // Get the valid question IDs for the current category+type selection
    const currentQuestions = category && type
      ? getQuestionsForIndustry(category, type)
      : [];
    const validKeys = new Set(currentQuestions.map(q => q.id));

    // Merge answers, then filter to only valid keys for the current selection
    const merged = { ...questionAnswers, ...multiSelect };
    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(merged)) {
      if (validKeys.has(key)) {
        filtered[key] = value;
      }
    }

    onChange({
      industry_category: category,
      industry_category_other: categoryOther || undefined,
      industry_type: type,
      industry_type_other: typeOther || undefined,
      industry_specific: filtered,
    });
  };

  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedType('');
    setOtherTypeText('');
    // Clear category other text unless selecting "other" category
    if (categoryId !== 'other') {
      setOtherCategoryText('');
    }
    setView('type');
    saveData(categoryId, categoryId === 'other' ? otherCategoryText : '', '', '', answers, multiSelectValues);
  };

  // Handle type selection
  const handleTypeSelect = (typeId: string) => {
    setSelectedType(typeId);
    setView('questions');
    saveData(selectedCategory, otherCategoryText, typeId, otherTypeText, answers, multiSelectValues);
  };

  // Handle going back to category selection
  const handleBackToCategory = () => {
    setView('category');
  };

  // Handle going back to type selection
  const handleBackToType = () => {
    setView('type');
  };

  // Update answer and save immediately for discrete choices
  const updateAnswerAndSave = (key: string, value: unknown) => {
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    saveData(selectedCategory, otherCategoryText, selectedType, otherTypeText, newAnswers, multiSelectValues);
  };

  // Update answer without saving (for text inputs - save on blur)
  const updateAnswer = (key: string, value: unknown) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  // Save current state (for blur events)
  const saveCurrentState = () => {
    saveData(selectedCategory, otherCategoryText, selectedType, otherTypeText, answers, multiSelectValues);
  };

  // Toggle multiselect and save immediately
  const toggleMultiSelect = (key: string, option: string) => {
    const current = multiSelectValues[key] || [];
    const updated = current.includes(option)
      ? current.filter(v => v !== option)
      : [...current, option];
    const newMultiSelect = { ...multiSelectValues, [key]: updated };
    setMultiSelectValues(newMultiSelect);
    saveData(selectedCategory, otherCategoryText, selectedType, otherTypeText, answers, newMultiSelect);
  };

  // Update other type text
  const updateOtherText = (text: string) => {
    setOtherTypeText(text);
  };

  // Check if we can proceed
  const canProceed = () => {
    if (!selectedCategory || !selectedType) return false;

    // Check if "Other" category requires text
    if (selectedCategory === 'other' && !otherCategoryText.trim()) return false;

    // Check if "Other" type requires text
    if (selectedType.endsWith('-other') && !otherTypeText.trim()) return false;

    // Check required questions
    const requiredQuestions = questions.filter(q => q.required);
    for (const q of requiredQuestions) {
      if (q.type === 'multiselect') {
        if (!multiSelectValues[q.id] || multiSelectValues[q.id].length === 0) return false;
      } else {
        if (!answers[q.id]) return false;
      }
    }

    return true;
  };

  // Get icon component by name
  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  // -------------------------------------------------------------------------
  // RENDER: Category Selection
  // -------------------------------------------------------------------------
  if (view === 'category') {
    return (
      <div className="space-y-8">
        {/* Welcome banner when this is the first step */}
        {isFirstStep && businessName && (
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-2xl p-6 text-center -mx-4 sm:mx-0">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">
              Welcome, {businessName}!
            </h1>
            <p className="text-primary-100">
              Let's get started by learning about your business.
            </p>
          </div>
        )}

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            What type of business do you have?
          </h2>
          <p className="text-gray-600">
            Select the category that best describes your business
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {INDUSTRY_TAXONOMY.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategorySelect(category.id)}
              className={`
                p-4 rounded-xl border-2 text-left transition-all hover:shadow-md
                ${selectedCategory === category.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300'
                }
              `}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="text-primary-600">
                  {getIcon(category.icon)}
                </div>
                <span className="font-semibold text-gray-900">{category.label}</span>
              </div>
              <p className="text-sm text-gray-500 line-clamp-2">{category.description}</p>
            </button>
          ))}
        </div>

        {/* Only show back button if not the first step */}
        {!isFirstStep && (
          <div className="flex justify-between pt-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div /> {/* Spacer */}
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: Type Selection
  // -------------------------------------------------------------------------
  if (view === 'type' && currentCategory) {
    const isOtherCategory = selectedCategory === 'other';

    return (
      <div className="space-y-8">
        <div>
          <button
            onClick={handleBackToCategory}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to categories
          </button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {currentCategory.label}
            </h1>
            <p className="text-gray-600">
              {isOtherCategory
                ? "Tell us what type of business you have"
                : "Select the specific type that best matches your business"
              }
            </p>
          </div>
        </div>

        {/* If "Other" category, show text input for category specification */}
        {isOtherCategory && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What industry or category does your business fall under?
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={otherCategoryText}
              onChange={(e) => setOtherCategoryText(e.target.value)}
              onBlur={saveCurrentState}
              placeholder="e.g., Event planning, Pet services, Technology consulting..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-amber-700 mt-2">
              This helps us tailor your website content and design to your specific industry.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {currentCategory.types.map((type) => (
            <button
              key={type.id}
              onClick={() => handleTypeSelect(type.id)}
              disabled={isOtherCategory && !otherCategoryText.trim()}
              className={`
                p-4 rounded-xl border-2 text-left transition-all hover:shadow-md
                ${selectedType === type.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300'
                }
                ${isOtherCategory && !otherCategoryText.trim()
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
                }
              `}
            >
              <span className="font-medium text-gray-900">{type.label}</span>
              {type.suggestedCredentials && type.suggestedCredentials.length > 0 && (
                <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                  {type.suggestedCredentials.slice(0, 2).join(', ')}
                </p>
              )}
            </button>
          ))}
        </div>

        {/* Only show back button if not the first step */}
        {!isFirstStep && (
          <div className="flex justify-between pt-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div /> {/* Spacer */}
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: Industry-specific Questions
  // -------------------------------------------------------------------------
  const selectedTypeData = currentCategory?.types.find(t => t.id === selectedType);
  const isOtherType = selectedType.endsWith('-other');

  return (
    <div className="space-y-8">
      <div>
        <button
          onClick={handleBackToType}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to {currentCategory?.label}
        </button>
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm mb-3">
            {currentCategory?.label} • {selectedTypeData?.label}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tell us more about your business
          </h1>
          <p className="text-gray-600">
            This helps us create the perfect website for you
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* If "Other" type, show text input for specification */}
        {isOtherType && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please specify your business type
              <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              type="text"
              value={otherTypeText}
              onChange={(e) => updateOtherText(e.target.value)}
              onBlur={saveCurrentState}
              placeholder="e.g., Mobile notary, Pet photography..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}

        {/* Render dynamic questions */}
        {questions.map((question) => (
          <div key={question.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.label}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {question.hint && (
              <p className="text-xs text-gray-500 mb-2">{question.hint}</p>
            )}

            {question.type === 'text' && (
              <input
                type="text"
                value={(answers[question.id] as string) || ''}
                onChange={(e) => updateAnswer(question.id, e.target.value)}
                onBlur={saveCurrentState}
                placeholder={question.placeholder}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            )}

            {question.type === 'textarea' && (
              <textarea
                value={(answers[question.id] as string) || ''}
                onChange={(e) => updateAnswer(question.id, e.target.value)}
                onBlur={saveCurrentState}
                placeholder={question.placeholder}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            )}

            {question.type === 'select' && (
              <select
                value={(answers[question.id] as string) || ''}
                onChange={(e) => updateAnswerAndSave(question.id, e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select an option</option>
                {question.options?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            )}

            {question.type === 'boolean' && (
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={question.id}
                    checked={answers[question.id] === true}
                    onChange={() => updateAnswerAndSave(question.id, true)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={question.id}
                    checked={answers[question.id] === false}
                    onChange={() => updateAnswerAndSave(question.id, false)}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-gray-700">No</span>
                </label>
              </div>
            )}

            {question.type === 'multiselect' && (
              <div className="flex flex-wrap gap-2">
                {question.options?.map((option) => {
                  const isSelected = (multiSelectValues[question.id] || []).includes(option);
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleMultiSelect(question.id, option)}
                      className={`
                        px-4 py-2 rounded-lg text-sm font-medium transition-all
                        ${isSelected
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Show suggested credentials if available */}
        {selectedTypeData?.suggestedCredentials && selectedTypeData.suggestedCredentials.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-medium text-blue-800 mb-2">
              Common credentials for {selectedTypeData.label}:
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedTypeData.suggestedCredentials.map((cred) => (
                <span
                  key={cred}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {cred}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed() || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${canProceed()
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
